import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { lazy, Suspense } from 'react';
import { RecordingsListSkeleton } from '@/components/recordings/RecordingsSkeletons';
import { requireAlfieCapability } from '@/api/alfie-plan';

const Recordings = lazy(() => import('@/pages/Recordings'));

const recordingsSearchSchema = z.object({
  // Series facet id from buildSeriesFacets: a slug of the series NAME
  // ('monday-mastermind'), so the link survives the series it names having
  // several parent rows behind it. The detail page links back here as
  // /recordings?series=<seriesId>. An unknown value simply matches nothing.
  // Omitted = "All".
  series: z.string().optional(),
  // Free-text search. Omitted (not `q=`) when empty, so the default state has
  // a clean URL — same "defaults encoded by omission" rule as /events.
  q: z.string().optional(),
});

export type RecordingsSearchParams = z.infer<typeof recordingsSearchSchema>;

export const Route = createFileRoute('/recordings/')({
  // Recordings are community content, so they ride the same paid-plan gate as
  // /community and /events — Free goes to upgrade.
  beforeLoad: ({ context: { queryClient } }) =>
    requireAlfieCapability(queryClient, 'community'),
  // Explicit param + return types: with beforeLoad present, an untyped
  // `(search) => schema.parse(search)` collapses the inferred search type to {}.
  validateSearch: (search: Record<string, unknown>): RecordingsSearchParams =>
    recordingsSearchSchema.parse(search),
  // `beforeLoad` runs BEFORE the component exists and TanStack holds the
  // previous page on screen until it resolves, so a Suspense fallback alone
  // can't cover it. Same skeleton for both phases (gate, then chunk) so the
  // wait reads as one continuous loading state.
  pendingComponent: RecordingsListSkeleton,
  component: () => (
    <Suspense fallback={<RecordingsListSkeleton />}>
      <Recordings />
    </Suspense>
  ),
});
